import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { Field, Fields, reduxForm, registerField } from 'redux-form';

import cls from 'classnames/bind';

import _debounce from 'lodash/debounce';
import qs from 'qs';

import { getAvailableTests } from '@ott/ab-test';
import Datepicker from '@ott/datepicker';
import DatepickerHeader from '@ott/datepicker-header';
import GeoAutocompleteNext from '@ott/geo-autocomplete-next';
import { withLayoutContext } from '@ott/layout-context';
import QuickTags from '@ott/quick-tags';
import SearchHistory, {
  getRoutesFromLocalStorage,
  saveSearchRouteInLocalStorage,
} from '@ott/search-history';
import { utility } from '@ott/shared-railways-logic';
import { stringifyDateReverse } from '@ott/utility-date';
import { withEnterSubmit } from '@ott/utility-enter-submit';

import SearchForm from './blocks/SearchForm';
import TravellersWrapper from './blocks/TravellersWrapper';
import { MOSCOW_GEO, SAINT_PETERSBURG_GEO } from './constants/constants';
import getAges from './reducers/ages/actions/get';
import { checkRoundTrip } from './reducers/roundtrip/actions/check';
import suggestActions from './reducers/suggest/actions';
import { pickPassengersValues } from './selectors/common';
import searchFormSelector from './selectors/searchForm';
import createDate from './utility/createDate';
import deleteInfantFromTotalChildren from './utility/deleteInfantFromTotalChildren';
import { focusField } from './utility/focusField';
import getMaxDate from './utility/getMaxDate';
import { getSearchRoute } from './utility/getRoute';
import getSuggestOptions, { remapCountry } from './utility/getSuggestOptions';
import { checkSearchHistory } from './utility/searchHistory';
import validate from './utility/validate';
import withLocationHook from './utility/withLocationHook';

import Cookie from 'js-cookie';

import s from './RailwaysSearchForm.scss';

const { reset: resetSuggest, suggest: getSuggest } = suggestActions;

const cx = cls.bind(s);

const MAX_PASSENGERS = 4;
const SEARCH_LENGTH_BOUNDARY = 1;
const SEARCH_HISTORY_ITEMS_COUNT = 2;

const DATEPICKER_OPTIONS = [
  { name: 'В одну сторону', value: false },
  { name: 'Туда-обратно', value: true },
];

const focusDictionary = {
  from: 'toText',
  to: 'dates',
  dates: 'travellers',
};

const maxDate = getMaxDate();

const FORM_NAME = 'RailwaysSearchForm';
const formConfig = {
  form: FORM_NAME,
  validate,
  touchOnBlur: false,
};

@withRouter
@withLayoutContext
@connect(searchFormSelector)
@reduxForm(formConfig)
@withEnterSubmit({ shouldFieldHasFocus: true })
class RailwaysSearchForm extends PureComponent {
  static propTypes = {
    fromItems: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.object)),
    toItems: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.object)),
    change: PropTypes.func,
    formValues: PropTypes.objectOf(PropTypes.oneOfType(PropTypes.object)),
    formErrors: PropTypes.objectOf(PropTypes.oneOfType(PropTypes.object)),
    submitFailed: PropTypes.bool,
    getAges: PropTypes.func,
    ages: PropTypes.shape({
      child: PropTypes.shape({
        age: PropTypes.number,
      }),
      infant: PropTypes.shape({
        age: PropTypes.number,
      }),
      babies: PropTypes.shape({
        age: PropTypes.number,
      }),
    }),
    isTravellersSkippable: PropTypes.bool,
    location: PropTypes.objectOf(PropTypes.oneOfType(PropTypes.object)).isRequired,
    passengersValues: PropTypes.objectOf(PropTypes.oneOfType(PropTypes.object)),
    timetableSearchQuery: PropTypes.objectOf(PropTypes.oneOfType(PropTypes.object)),
    roundTripStatus: PropTypes.objectOf(PropTypes.oneOfType(PropTypes.object)),
  };

  static defaultProps = {
    formValues: {},
    formErrors: {},
    isTravellersSkippable: false,
  };

  constructor(props, ctx) {
    super(props, ctx);

    this.state = {
      didMount: false,
    };

    this.handleFromAutocompleteText = this.handleAutocompleteText('from');
    this.handleToAutocompleteText = this.handleAutocompleteText('to');

    this.handleFromOptionFocus = this.handleAutocompleteFocus('from');
    this.handleToOptionFocus = this.handleAutocompleteFocus('to');

    this.handleFromOptionSelect = this.handleOptionSelect('from');
    this.handleToOptionSelect = this.handleOptionSelect('to');
    this.handleDatesSelect = this.handleDateSelect('dates');
  }

  UNSAFE_componentWillMount() {
    const { dispatch } = this.props;

    dispatch(registerField(FORM_NAME, 'from', 'Field'));
    dispatch(registerField(FORM_NAME, 'to', 'Field'));
  }

  componentDidMount() {
    const { initialValues, initialValuesAsync, dispatch } = this.props;

    if (initialValuesAsync) {
      initialValuesAsync.then((asyncValues) => {
        // reset + initialize работать не хочет
        Object.entries(asyncValues).forEach(([key, value]) => {
          dispatch(this.props.change(key, value));
        });

        this.onInitialize(asyncValues);
      });

      return;
    }

    this.onInitialize(initialValues || {});
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const {
      roundTripStatus,
      formValues: { children = 0, infants = 0, babies = 0, totalChildren = [] } = {},
      dispatch,
    } = nextProps;

    const { from, to, dates } = nextProps.formValues;
    const { id: fromId, countryId: fromCountryId, countryCode: fromCountryCode } = from || {};
    const { id: toId, countryId: toCountryId, countryCode: toCountryCode } = to || {};

    const { formValues: prevFormValues, roundTripStatus: prevRTStatus, change } = this.props;

    const { from: prevFrom, to: prevTo } = prevFormValues;
    const { id: prevFromId, countryId: prevFromCountryId } = prevFrom || {};
    const { id: prevToId, countryId: prevToCountryId } = prevTo || {};

    const { startDate } = dates || {};

    const hasAnyCountryChanged =
      fromCountryId &&
      toCountryId &&
      (prevFromCountryId !== fromCountryId || prevToCountryId !== toCountryId);

    const hasAnyCodesChanged = fromId && toId && (prevFromId !== fromId || prevToId !== toId);

    if (
      roundTripStatus.status.success &&
      roundTripStatus.available === false &&
      prevRTStatus.available !== roundTripStatus.available
    ) {
      change('roundTrip', false);
      change('dates', {
        startDate,
      });
    }

    if (hasAnyCodesChanged) {
      const { ages } = dispatch(
        getAges({
          fromCode: fromCountryCode,
          toCode: toCountryCode,
        })
      );

      const childrenCount = children + infants + babies;
      if (!Number.isNaN(childrenCount) && childrenCount !== totalChildren.length) {
        const newTotalChildren = [].concat(
          new Array(children).fill(ages.childAge - 1),
          new Array(infants).fill(ages.infantAge - 1),
          new Array(babies).fill(ages.babyAge - 1)
        );
        change('totalChildren', newTotalChildren);
      }
    }

    if (hasAnyCountryChanged || (hasAnyCodesChanged && roundTripStatus.status.error)) {
      dispatch(checkRoundTrip(toCountryId, fromCountryId));
    }
  }

  componentWillUnmount() {
    const { dispatch } = this.props;
    dispatch(resetSuggest('from'));
    dispatch(resetSuggest('to'));
  }

  onInitialize = (values) => {
    const { dispatch, change } = this.props;

    const { from = {}, to = {}, dates } = values || {};

    if (to.countryId && from.countryId) {
      dispatch(checkRoundTrip(to.countryId, from.countryId));
    }

    if (!from.countryId) {
      this.getTopSuggest('from', from, to);
    }

    if (!to.countryId) {
      this.getTopSuggest('to', from, to);
    }

    if (!dates.startDate && from.countryId && to.countryId) {
      dispatch(
        change('dates', {
          startDate: createDate(1),
        })
      );

      this.focusField('dates');
    }

    this.setState({
      didMount: true,
    });

    const abst = Cookie.get('abst');
    this.hideRoundTripDiscount = abst && abst.includes('rrt_b');
  };

  prepareSearchHistoryItems() {
    const routes =
      getRoutesFromLocalStorage(
        'railwaysHistoryRoutes',
        SEARCH_HISTORY_ITEMS_COUNT,
        checkSearchHistory
      ) || [];

    return routes.map((data) => {
      const route = (data && data.route) || {};
      const query = (data && data.query) || {};

      const { from, to, dates } = route;

      return {
        from: from.name,
        to: to.name,
        fromDate: dates.startDate,
        toDate: dates.endDate,
        isRT: Boolean(dates.startDate && dates.endDate),
        url: getSearchRoute({
          departure: from.trans,
          arrival: to.trans,
          date: new Date(dates.startDate),
          returnDate: dates.endDate ? new Date(dates.endDate) : undefined,
          passengersValues: {
            adults: query.adults,
            children: query.children,
            infants: query.infants,
            babies: query.babies,
            totalChildren: query.totalChildren,
          },
        }),
        data,
      };
    });
  }

  focusField(fieldName) {
    if (fieldName === 'travellers' && this.props.isTravellersSkippable) {
      return;
    }
    // После обновления реакта до 17 версии handleClickOutside в компонентах Travellers и Datepicker перестал корректно работать:
    // клик при выборе значения предыдущего поля инициализирует фокус на следующем поле и последующую установку хендлера handleClickOutside,
    // при этом сам клик успевает отловиться этим же хендлером, что вызывает закрытие оверлея у Travellers или Datepicker.
    // Поэтому приходится обернуть фокусировку в setTimeOut
    setTimeout(() => focusField(fieldName));
  }

  getTopSuggest(fieldName, from, to) {
    const { dispatch } = this.props;

    const params = {
      slug: fieldName,
      mode: `top_${fieldName}`,
      lang: 'ru',
    };

    params.from = fieldName === 'to' ? from && from.id : to && to.id;

    dispatch(getSuggest(params));
  }

  handleAutocompleteFocus = (fieldName) => () => {
    const {
      formValues: { from, to },
    } = this.props;

    if (fieldName === 'from' && (!from || from.countryId)) {
      this.getTopSuggest('from', from, to);
    }

    if (fieldName === 'to' && (!to || !to.countryId)) {
      this.getTopSuggest('to', from, to);
    }
  };

  handleAutocompleteText = (fieldName) =>
    _debounce((text) => {
      const {
        dispatch,
        formValues: { from, to },
      } = this.props;

      if ((!text || text.length < SEARCH_LENGTH_BOUNDARY) && text.toLowerCase() !== 'ош') {
        this.getTopSuggest(fieldName, from, to);

        return;
      }

      dispatch(
        getSuggest({
          slug: fieldName,
          text,
          type: 'station',
        })
      );
    }, 300);

  handleOptionSelect = (fieldName) => (option, params) => {
    const {
      dispatch,
      change,
      formValues: { from, to },
      isMobileLayout,
      isTabletLayout,
    } = this.props;

    const { id } = option || {};

    /**
     *  был вынужден сделать через spread, чтобы триггер обновлялся.
     *  вызвано тем, что fromText обновляется тогда, когда обновляется from,
     *  если выбрать вариант из саджеста, то оно запишется в from и fromText,
     *  потом если стереть fromText, то инпут будет пустым, но значение from останется прежним,
     *  и когда мы заченджим с таким же from, fromText не обновится и так же останется пустым
     */
    change(fieldName, { ...option });

    dispatch(resetSuggest(fieldName));

    if (params) {
      if (params.selectNext && !isMobileLayout && !isTabletLayout) {
        setTimeout(() => this.focusField(focusDictionary[fieldName]));
      }
      if (params.popularItem && id) {
        const isFrom = fieldName === 'from';
        const result = {
          type: isFrom ? 'topFrom' : 'topTo',
        };

        if (isFrom) {
          result.cityFrom = id;
          result.cityTo = (to && to.id) || '';
        } else {
          result.cityFrom = (from && from.id) || '';
          result.cityTo = id;
        }

        utility.KM('rzd_suggest_click', {
          obj: result,
        });
      }
    }
  };

  handleAutocompleteClose = (type) => {
    const { dispatch } = this.props;
    dispatch(resetSuggest(type));
  };

  handleSwapDirections = () => {
    const { dispatch, formValues, change } = this.props;

    dispatch(resetSuggest('from'));
    dispatch(resetSuggest('to'));

    change('from', formValues.to);
    change('to', formValues.from);
  };

  handleStartDateSelectFromTags = (startDate) => {
    const { change } = this.props;

    change('dates', {
      startDate,
    });
  };

  handleDateSelect =
    (fieldName) =>
    (startDate, endDate, byCloseButton, shouldFocusNext = true) => {
      const {
        change,
        formValues: { roundTrip },
        isMobileLayout,
        isTabletLayout,
      } = this.props;

      if (!roundTrip) {
        // eslint-disable-next-line no-param-reassign
        endDate = undefined;
      }

      change(fieldName, {
        startDate,
        endDate,
      });

      if (byCloseButton) {
        return;
      }

      if (isMobileLayout || isTabletLayout) {
        return;
      }

      if (shouldFocusNext && (!roundTrip || (startDate && endDate))) {
        this.focusField(focusDictionary[fieldName]);
      }
    };

  handleAdultsChange = (value) => {
    const { change, formValues, ages } = this.props;

    // TODO: Where is constants?
    if (formValues.adults === 2 && value === 1 && formValues.infants === 2) {
      deleteInfantFromTotalChildren(formValues.totalChildren, ages, change);
      change('infants', 1);
    } else if (formValues.adults === 1 && value === 0 && formValues.infants === 1) {
      deleteInfantFromTotalChildren(formValues.totalChildren, ages, change);
      change('infants', 0);
    }

    change('adults', value);
  };

  handleTotalChildrenChange = (values) => {
    const { ages, change } = this.props;

    let infants = 0;
    let children = 0;
    let babies = 0;
    values.forEach((value) => {
      if (value < ages.baby.age) {
        babies += 1;
      } else if (value < ages.infant.age) {
        infants += 1;
      } else {
        children += 1;
      }
    });

    change('infants', infants);
    change('children', children);
    change('babies', babies);
    change('totalChildren', values);
  };

  handleSubmit = async (values) => {
    const {
      from,
      to,
      dates,
      dates: { startDate: date, endDate: returnDate },
      adults,
      children,
      infants,
      babies,
      totalChildren,
    } = values;

    const {
      location: { search },
      formErrors,
      onFormSubmit,
    } = this.props;

    if (Object.keys(formErrors).length) {
      return;
    }

    const passengersValues = pickPassengersValues(values);

    const query = qs.parse(search, { ignoreQueryPrefix: true });

    const data = {
      route: {
        from: remapCountry(from),
        to: remapCountry(to),
        dates,
      },
      query: {
        adults,
        children,
        infants,
        babies,
        totalChildren,
      },
    };

    saveSearchRouteInLocalStorage(
      data,
      'railwaysHistoryRoutes',
      SEARCH_HISTORY_ITEMS_COUNT,
      checkSearchHistory
    );

    utility.KM(`RZD_SEARCH_FORM_FIND_BUTTON_CLICK`, {
      obj: {
        totalCount: Number(adults) + Number(children) + Number(infants) + Number(babies),
        tripType: returnDate ? 'RT' : 'OW',
        cityTo: to.id,
        cityFrom: to.id,
      },
    });

    onFormSubmit({
      departure: from.trans,
      arrival: to.trans,
      vk: query.vk,
      passengersValues,
      date: stringifyDateReverse(date),
      returnDate: returnDate && stringifyDateReverse(returnDate),
    });
  };

  handleRoundTripChange = (roundTrip) => {
    const {
      change,
      formValues: { dates },
    } = this.props;

    const { startDate } = dates || {};

    if (!roundTrip) {
      change('dates', {
        startDate,
      });
    }

    change('roundTrip', roundTrip);
  };

  handleHistorySearch = (item) => {
    const { onFormSubmit } = this.props;

    const {
      data: {
        route: {
          from,
          to,
          dates: { startDate, endDate },
        },
        query: { adults, children, infants, babies, totalChildren },
      },
    } = item;

    saveSearchRouteInLocalStorage(
      item.data,
      'railwaysHistoryRoutes',
      SEARCH_HISTORY_ITEMS_COUNT,
      checkSearchHistory
    );

    onFormSubmit({
      departure: from.trans,
      arrival: to.trans,
      passengersValues: {
        adults,
        children,
        infants,
        babies,
        totalChildren,
      },
      date: stringifyDateReverse(startDate),
      returnDate: endDate && stringifyDateReverse(endDate),
    });
  };

  renderAutocomplete = (field) => {
    const { input, meta, type, triggerProps, overlayProps, ...props } = field;

    return (
      <GeoAutocompleteNext
        triggerProps={{
          ...input,
          ...triggerProps,
          type,
          initial: meta.initial,
        }}
        overlayProps={{
          ...overlayProps,
          optionRendererProps: {
            ...overlayProps.optionRendererProps,
            className: s.option,
          },
        }}
        {
          // eslint-disable-next-line react/jsx-props-no-spreading
          ...props
        }
        withArrow={false}
        onClose={this.handleAutocompleteClose}
      />
    );
  };

  renderSearchFormTagsBar = () => {
    const {
      formValues: { roundTrip, dates },
      isDesktopLayout,
    } = this.props;

    const searchHistoryItems = this.prepareSearchHistoryItems();
    const showHistory = searchHistoryItems.length > 0 || !isDesktopLayout;

    const { startDate } = dates || {};

    const preparedDateTags = [
      {
        title: 'Сегодня',
        date: createDate(),
      },
      {
        title: 'Завтра',
        date: createDate(1),
      },
      {
        title: 'Послезавтра',
        date: createDate(2),
      },
    ];

    return showHistory ? (
      <SearchHistory
        className={cx('history')}
        handleSearch={this.handleHistorySearch}
        items={searchHistoryItems}
        project="railways"
      />
    ) : (
      <QuickTags
        destinations={[MOSCOW_GEO, SAINT_PETERSBURG_GEO]}
        onFromSelect={this.handleFromOptionSelect}
        onToSelect={this.handleToOptionSelect}
        onStartDateSelect={this.handleStartDateSelectFromTags}
        showToTags={true}
        showDateTags={!roundTrip}
        startDate={startDate}
        dateTags={preparedDateTags}
        childrenClassNames={{
          fromTags: cx('fromTags'),
        }}
      />
    );
  };

  render() {
    const {
      handleSubmit,
      submitting,
      submitSucceeded,
      submitFailed,
      formErrors,
      ages,
      formValues,
      formValues: { from = {}, to = {}, roundTrip },
      initialValues,
      isMobileLayout,
      isTravellersSkippable,
      roundTripStatus: { available: roundTripAvailable },
      fromSuggest,
      toSuggest,
      formRef,
      submitRef,
    } = this.props;

    const fromItems = getSuggestOptions(fromSuggest.items || from);
    const toItems = getSuggestOptions(toSuggest.items || to);

    const fromField = (
      <Field
        name="fromText"
        component={this.renderAutocomplete}
        triggerProps={{
          placeholder: 'Откуда',
          title: 'Откуда',
          onFocus: this.handleFromOptionFocus,
          onSearch: this.handleFromAutocompleteText,
        }}
        overlayProps={{
          title: 'Откуда',
          options: fromItems, // временно отключаем ? [groupOption, ...fromItems] : fromItems}
          specificLocators: 'geo-autocomplete-from-options',
        }}
        props={{
          value: from,
          onChange: this.handleFromOptionSelect,
          isLoading: fromSuggest.status.loading,
          specificLocators: 'search-from',
        }}
      />
    );

    const toField = (
      <Field
        name="toText"
        component={this.renderAutocomplete}
        triggerProps={{
          placeholder: 'Куда',
          title: 'Куда',
          onFocus: this.handleToOptionFocus,
          onSearch: this.handleToAutocompleteText,
        }}
        overlayProps={{
          title: 'Куда',
          options: toItems, // ? [groupOption, ...toItems] : toItems}
          specificLocators: 'geo-autocomplete-to-options',
        }}
        props={{
          value: to,
          onChange: this.handleToOptionSelect,
          isLoading: toSuggest.status.loading,
          specificLocators: 'search-to',
        }}
      />
    );

    const datesField = (
      <Field
        name="dates"
        component={Datepicker}
        placeholder="Когда"
        maxDate={maxDate}
        endDatePlaceholder="Обратно"
        roundTripActive={roundTrip}
        roundTripAvailable={roundTripAvailable}
        onDateSelect={this.handleDatesSelect}
        closeAfterSelect={isTravellersSkippable}
        pickOneDate={!roundTripAvailable || !roundTrip}
        errorPlacement="top"
        shouldShowTomorrow={getAvailableTests().includes('r37_b')}
      >
        {roundTripAvailable && (
          <DatepickerHeader
            options={DATEPICKER_OPTIONS}
            defaultOptionIndex={roundTrip ? 1 : 0}
            onOptionSelect={this.handleRoundTripChange}
          >
            {!this.hideRoundTripDiscount && (
              <div className={cx('datepickerHeaderInfo')}>до -20% на обратный билет</div>
            )}
          </DatepickerHeader>
        )}
      </Field>
    );

    const travellersField = (
      <Fields
        names={['totalChildren', 'adults']}
        component={TravellersWrapper}
        props={{
          ages,
          formValues,
          maxPassengers: MAX_PASSENGERS,
          onAdultsChange: this.handleAdultsChange,
          onTotalChildrenChange: this.handleTotalChildrenChange,
          isAbsoluteOverlayPosition: !isMobileLayout,
        }}
      />
    );

    return (
      <div className={cx('RailwaysSearchForm')}>
        <SearchForm
          formRef={formRef}
          submitRef={submitRef}
          onSubmit={handleSubmit(this.handleSubmit)}
          onSwapDirectionsClick={this.handleSwapDirections}
          fields={{
            from: fromField,
            to: toField,
            dates: datesField,
            travellers: travellersField,
          }}
          formValues={formValues}
          initialValues={initialValues}
          isMobileLayout={isMobileLayout}
          errors={formErrors}
          showErrors={submitFailed || submitSucceeded || submitting}
          customButtonText={
            isMobileLayout && getAvailableTests().includes('r38_b') && 'Найти ж/д билеты'
          }
        />
        {this.state.didMount && this.renderSearchFormTagsBar()}
      </div>
    );
  }
}

export default withLocationHook(RailwaysSearchForm);
